<template>
  <v-row v-if="showEnable" no-gutters>
    <v-col>
      <v-text-field
        v-model="fieldValue"
        :variant="variant"
        :label="props.textLabel"
        :disabled="isDisconnected"
        :loading="isLoading"
        :readonly="isReadOnly"
        :error-messages="error_msg"
        @keydown="onKeyDown"
        @blur="onBlur"
      />
    </v-col>
    <ConfigInputButtons
      :default-value="defaultValue"
      :is-loading="isLoading"
      :is-reload-disabled="isReloadDisabled"
      :is-set-default-disabled="isSetDefaultDisabled"
      :parameter="props.parameter"
      :show-buttons="props.showButtons"
      @reload-value="reloadValue"
      @set-default="setDefault"
    />
  </v-row>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import ConfigInputButtons from "../ConfigInputButtons.vue";
import { useByteUtils } from "@/composables/useByteUtils";
import { ParamField } from "../../enums/ParamField";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { ModbusParam } from "@/types/ModbusParam";
import { TimerId } from "@/types/TimerId";

const modbusStore = useModbusStore();
const { uint16_to_bytes } = useByteUtils();

const props = defineProps<{
  modbusReload?: boolean;
  parameter: ModbusParam;
  showButtons?: boolean;
  textItems: ModbusParam[];
  textLabel: string;
}>();

let reloadCount = 0;
let write_count = 0;
let write_value_array: number[] = [];

const modbusReqTimer = ref<TimerId>(0);
const fieldValue = ref<string>("");
const prevValue = ref();

const isReloadDisabled = computed<boolean>(() => isDefault.value);

const isSetDefaultDisabled = computed<boolean>(
  () => isDefault.value || isReadOnly.value,
);

const defaultValue = computed<string>(() =>
  props.textItems.reduce((acc, item) => `${acc}${item.default}`, ""),
);

const isLoading = computed(() => {
  if (
    !(props.parameter[ParamField.mbusAddress] in modbusStore.modbusAddrReadBusy)
  ) {
    return false;
  }
  const modbusAddress: number = parseInt(
    props.parameter[ParamField.mbusAddress],
  );
  let busy = false;
  for (let i = 0; i < props.textItems.length; i++) {
    busy = busy || modbusStore.modbusAddrReadBusy[modbusAddress + i];
  }
  return busy;
});

const error_msg = computed(() => {
  let errMsg = "";
  const modbusAddress: number = parseInt(
    props.parameter[ParamField.mbusAddress],
  );
  for (let i = 0; i < props.textItems.length; i++) {
    if (modbusStore.modbusAddrErrMsg[modbusAddress + i] !== "") {
      errMsg = modbusStore.modbusAddrErrMsg[modbusAddress + i];
      break;
    }
  }
  return errMsg;
});

const showEnable = computed(() => {
  return (
    props.parameter.type === "char2" &&
    modbusStore.isParamUserReadable(props.parameter)
  );
});

const isDisconnected = computed<boolean>(
  () => modbusStore.portState === PortState.disconnected,
);

const isReadOnly = computed(
  () => !modbusStore.isParamUserWriteable(props.parameter),
);

const modbusAddrReadBusy = computed(() => {
  return modbusStore.modbusAddrReadBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

const modbusAddrWriteBusy = computed(() => {
  return modbusStore.modbusAddrWriteBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

const isDefault = computed(() => {
  return fieldValue.value.indexOf(defaultValue.value) === 0;
});

const variant = computed(() => {
  return isReadOnly.value ? undefined : "underlined";
});

function updateValue() {
  getValue();
  prevValue.value = fieldValue.value;
}

function getValue() {
  let show_string = "";
  for (let i = 0; i < props.textItems.length; i++) {
    const reg_bytes = uint16_to_bytes(
      modbusStore.modbusValues[
        parseInt(props.parameter[ParamField.mbusAddress]) + i
      ],
    );
    if (reg_bytes[0] != 0) {
      show_string = show_string.concat(String.fromCharCode(reg_bytes[0]));
    }
    if (reg_bytes[1] != 0) {
      show_string = show_string.concat(String.fromCharCode(reg_bytes[1]));
    }
  }
  fieldValue.value = show_string;
}

function reloadValue() {
  reloadCount = 0;
  reload();
}

function reload() {
  if (modbusStore.storeInit && modbusStore.portState == PortState.connected) {
    if (modbusReqTimer.value != 0) {
      clearInterval(modbusReqTimer.value);
    }
    //-------- Device Info -------------------------
    modbusStore.modbusRead(
      modbusStore.deviceAddress,
      props.parameter[ParamField.mbusAddress],
      props.textItems.length,
    );
  }
}

function writeModbus() {
  modbusStore.modbusWrite(
    modbusStore.deviceAddress,
    props.parameter[ParamField.mbusAddress],
    props.textItems.length,
    new Uint8Array(write_value_array),
  );
}

function writeValue(write_value: string) {
  if (prevValue.value != write_value) {
    modbusStore.modbusIsChanged = false;
    const write_bytes = new Uint8Array(props.textItems.length * 2);
    for (let i = 0; i < write_value.length; i++) {
      write_bytes[i] = write_value.charCodeAt(i);
    }
    write_value_array = Array.from(write_bytes);
    writeModbus();
  }
}

function setDefault() {
  writeValue(defaultValue.value);
}

function onKeyDown(event: KeyboardEvent): void {
  const alwaysAllowedKeys = ["arrowleft", "arrowright", "backspace", "delete"];
  if (event.key.toLocaleLowerCase() === "enter") {
    writeValue(fieldValue.value);
    return;
  }
  if (alwaysAllowedKeys.includes(event.key.toLocaleLowerCase())) {
    // this keypress is allowed
    return;
  }
  const input = event.target as HTMLInputElement;
  const hasSelection = input.selectionStart !== input.selectionEnd;
  if (hasSelection) {
    // Typing will replace selected text, so allow it
    return;
  }
  if (fieldValue.value.length === props.textItems.length * 2) {
    event.preventDefault();
  }
}

function onBlur(): void {
  writeValue(fieldValue.value);
}

watch(isLoading, (newVal) => {
  if (!newVal) {
    updateValue();
  }
});

watch(modbusAddrReadBusy, (newVal) => {
  if (!newVal) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ==
      ""
    ) {
      reloadCount = 0;
    } else {
      if (reloadCount < modbusStore.maxReloadCount) {
        reload();
        reloadCount++;
      }
    }
  }
});

watch(modbusAddrWriteBusy, (newVal: boolean, oldVal: boolean) => {
  if (
    newVal === false &&
    oldVal !== undefined &&
    write_value_array.length > 0
  ) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ==
      ""
    ) {
      write_count = 0;
    } else {
      if (write_count < modbusStore.maxReloadCount) {
        writeModbus();
        write_count++;
      }
    }
  }
});

onMounted(() => {
  if (props.modbusReload) {
    modbusReqTimer.value = setInterval(async () => {
      if (
        modbusStore.storeInit &&
        modbusStore.portState == PortState.connected
      ) {
        clearInterval(modbusReqTimer.value);
        //-------- Device Info -------------------------
        modbusStore.modbusRead(
          modbusStore.deviceAddress,
          props.parameter[ParamField.mbusAddress],
          props.textItems.length,
        );
      }
    }, 200);
  } else {
    updateValue();
  }
});

onUnmounted(() => {
  if (modbusReqTimer.value) {
    clearInterval(modbusReqTimer.value);
  }
});
</script>
